import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, User, Search } from 'lucide-react';
import { useGalleryConfig } from '../../context/GalleryConfigContext';
import { useGalleryTheme } from '../../context/GalleryThemeContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { useGalleryCart } from '../../context/GalleryCartContext';
import Button from '../common/Button';

const GalleryHeader: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const location = useLocation();

  const { config } = useGalleryConfig();
  const { theme } = useGalleryTheme();
  const { customer, isAuthenticated, logout } = useCustomerAuth();
  const { cart } = useGalleryCart();

  const totalItems =
    cart.items.reduce((sum, item) => sum + item.quantity, 0) + cart.packages.length;

  const navItems = [
    { href: '/', label: 'Home' },
    { href: '/events', label: 'Galleries' },
  ];

  const isActivePath = (path: string) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  if (!config) return null;

  return (
    <header
      className="sticky top-0 z-50 shadow-md"
      style={{
        backgroundColor: theme.colors.surface,
        borderBottomColor: theme.colors.border,
      }}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo and Brand */}
          <Link to="/" className="flex items-center space-x-3 hover:opacity-80 transition-opacity">
            {config.branding.logo ? (
              <img
                src={config.branding.logo}
                alt={`${config.businessName} Logo`}
                className="h-8 w-auto"
              />
            ) : (
              <div
                className="h-8 w-8 rounded flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: theme.colors.primary }}
              >
                {config.businessName.charAt(0).toUpperCase()}
              </div>
            )}
            <span
              className="font-bold text-xl hidden sm:block"
              style={{ color: theme.colors.text.primary }}
            >
              {config.businessName}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map(item => (
              <Link
                key={item.href}
                to={item.href}
                className={`font-medium transition-colors ${
                  isActivePath(item.href) ? 'border-b-2' : 'hover:opacity-70'
                }`}
                style={{
                  color: isActivePath(item.href)
                    ? theme.colors.primary
                    : theme.colors.text.secondary,
                  borderBottomColor: isActivePath(item.href) ? theme.colors.primary : 'transparent',
                }}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Actions */}
          <div className="flex items-center space-x-4">
            {/* Search */}
            <button
              onClick={() => setIsSearchOpen(!isSearchOpen)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Search"
            >
              <Search size={20} style={{ color: theme.colors.text.secondary }} />
            </button>

            {/* Cart */}
            <Link
              to="/cart"
              className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label={`Shopping cart with ${totalItems} items`}
            >
              <ShoppingCart size={20} style={{ color: theme.colors.text.secondary }} />
              {totalItems > 0 && (
                <span
                  className="absolute -top-1 -right-1 h-5 w-5 rounded-full text-xs font-medium flex items-center justify-center text-white"
                  style={{ backgroundColor: theme.colors.primary }}
                >
                  {totalItems > 99 ? '99+' : totalItems}
                </span>
              )}
            </Link>

            {/* User Menu */}
            {isAuthenticated && customer ? (
              <div className="relative group">
                <button
                  className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="User menu"
                >
                  {customer.avatar ? (
                    <img
                      src={customer.avatar}
                      alt={customer.name}
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <User size={20} style={{ color: theme.colors.text.secondary }} />
                  )}
                  <span className="hidden sm:block text-sm font-medium">
                    {customer.name.split(' ')[0]}
                  </span>
                </button>

                {/* Dropdown Menu */}
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                  <div className="py-1">
                    <Link
                      to="/customers/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      My Account
                    </Link>
                    <Link
                      to="/customers/profile?tab=orders"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Order History
                    </Link>
                    <Link
                      to="/customers/profile?tab=favorites"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Favorites
                    </Link>
                    <button
                      onClick={logout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    Sign In
                  </Button>
                </Link>
                {config.settings.requireRegistration && (
                  <Link to="/register">
                    <Button variant="primary" size="sm">
                      Sign Up
                    </Button>
                  </Link>
                )}
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <X size={24} style={{ color: theme.colors.text.secondary }} />
              ) : (
                <Menu size={24} style={{ color: theme.colors.text.secondary }} />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t" style={{ borderTopColor: theme.colors.border }}>
            <nav className="space-y-4">
              {navItems.map(item => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`block font-medium transition-colors ${
                    isActivePath(item.href) ? '' : 'hover:opacity-70'
                  }`}
                  style={{
                    color: isActivePath(item.href)
                      ? theme.colors.primary
                      : theme.colors.text.secondary,
                  }}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}

              {isAuthenticated && customer && (
                <div
                  className="space-y-2 pt-4 border-t"
                  style={{ borderTopColor: theme.colors.border }}
                >
                  <Link
                    to="/customers/profile"
                    className="block font-medium transition-colors"
                    style={{ color: theme.colors.text.secondary }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    My Account
                  </Link>
                  <Link
                    to="/customers/profile?tab=orders"
                    className="block font-medium transition-colors"
                    style={{ color: theme.colors.text.secondary }}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Order History
                  </Link>
                </div>
              )}

              {!isAuthenticated && (
                <div
                  className="space-y-2 pt-4 border-t"
                  style={{ borderTopColor: theme.colors.border }}
                >
                  <Link to="/login" className="block" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="w-full">
                      Sign In
                    </Button>
                  </Link>
                  {config.settings.requireRegistration && (
                    <Link
                      to="/register"
                      className="block"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Button variant="primary" size="sm" className="w-full">
                        Sign Up
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </nav>
          </div>
        )}

        {/* Search Bar */}
        {isSearchOpen && (
          <div className="py-4 border-t" style={{ borderTopColor: theme.colors.border }}>
            <div className="relative">
              <input
                type="text"
                placeholder="Search photos, events..."
                className="w-full px-4 py-2 pr-10 border rounded-lg focus:outline-none focus:ring-2"
                style={{
                  borderColor: theme.colors.border,
                }}
                autoFocus
              />
              <button className="absolute right-3 top-1/2 transform -translate-y-1/2">
                <Search size={16} style={{ color: theme.colors.text.muted }} />
              </button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default GalleryHeader;
